import { ShieldCheck, Shield, Smartphone, MessageSquare, Key, AlertTriangle, RefreshCw, ShieldX, Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';

/**
 * Main 2FA Status Card
 * Displays current 2FA status and action buttons
 */
export function TwoFactorCard({
  status,
  smsAvailable,
  actionLoading,
  error,
  onSetupAuthenticator,
  onSetupSms,
  onOpenRegenerateDialog,
  onOpenDisableDialog
}) {
  const hasAny2FA = status.totp_enabled || status.sms_enabled;

  return (
    <Card className="border-2 hover:border-teal-200 transition-colors">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-3">
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center shadow-lg ${
              hasAny2FA 
                ? 'bg-gradient-to-br from-emerald-500 to-teal-600' 
                : 'bg-gradient-to-br from-slate-400 to-slate-500'
            }`}>
              {hasAny2FA ? (
                <ShieldCheck className="w-7 h-7 text-white" />
              ) : (
                <Shield className="w-7 h-7 text-white" />
              )}
            </div>
            <div>
              <CardTitle className="text-xl">To-faktor autentisering</CardTitle>
              <CardDescription>
                {hasAny2FA 
                  ? 'Kontoen din er beskyttet med 2FA'
                  : 'Legg til et ekstra lag med sikkerhet'}
              </CardDescription>
            </div>
          </div>
          <Badge 
            variant={hasAny2FA ? 'default' : 'secondary'}
            className={hasAny2FA ? 'bg-emerald-500' : ''}
          >
            {hasAny2FA ? 'Aktivert' : 'Ikke aktivert'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Authenticator App Section */}
        <AuthenticatorSection
          enabled={status.totp_enabled}
          actionLoading={actionLoading}
          onSetup={onSetupAuthenticator}
        />

        {/* SMS Section */}
        {smsAvailable && (
          <SmsSection
            enabled={status.sms_enabled}
            phoneNumber={status.phone_number}
            actionLoading={actionLoading}
            onSetup={onSetupSms}
          />
        )}

        {/* Backup codes info (only if TOTP is enabled) */}
        {status.totp_enabled && (
          <BackupCodesSection
            backupCodesRemaining={status.backup_codes_remaining}
            onOpenRegenerateDialog={onOpenRegenerateDialog}
            onOpenDisableDialog={onOpenDisableDialog}
          />
        )}

        {/* Not enabled state */}
        {!hasAny2FA && <WhyUse2FASection />}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-600">
            {error}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AuthenticatorSection({ enabled, actionLoading, onSetup }) {
  return (
    <div className={`p-4 rounded-lg border-2 ${enabled ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-slate-50'}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${enabled ? 'bg-emerald-500' : 'bg-slate-400'}`}>
            <Smartphone className="w-5 h-5 text-white" />
          </div>
          <div>
            <h4 className="font-medium text-slate-900">Autentiseringsapp</h4>
            <p className="text-sm text-slate-600">
              {enabled ? 'Aktivert' : 'Google Authenticator, Microsoft Authenticator, etc.'}
            </p>
          </div>
        </div>
        {enabled ? (
          <Badge className="bg-emerald-500">
            <Check className="w-3 h-3 mr-1" />
            Aktiv
          </Badge>
        ) : (
          <Button
            onClick={onSetup}
            disabled={actionLoading}
            size="sm"
            className="bg-teal-600 hover:bg-teal-700"
          >
            {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Aktiver'}
          </Button>
        )}
      </div>
    </div>
  );
}

function SmsSection({ enabled, phoneNumber, actionLoading, onSetup }) {
  return (
    <div className={`p-4 rounded-lg border-2 ${enabled ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-slate-50'}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${enabled ? 'bg-emerald-500' : 'bg-slate-400'}`}>
            <MessageSquare className="w-5 h-5 text-white" />
          </div>
          <div>
            <h4 className="font-medium text-slate-900">SMS-verifisering</h4>
            <p className="text-sm text-slate-600">
              {enabled 
                ? `Telefon: ${phoneNumber}` 
                : 'Få kode på SMS til din telefon'}
            </p>
          </div>
        </div>
        {enabled ? (
          <Badge className="bg-emerald-500">
            <Check className="w-3 h-3 mr-1" />
            Aktiv
          </Badge>
        ) : (
          <Button
            onClick={onSetup}
            disabled={actionLoading}
            size="sm"
            variant="outline"
            className="border-teal-600 text-teal-600 hover:bg-teal-50"
          >
            {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Aktiver'}
          </Button>
        )}
      </div>
    </div>
  );
}

function BackupCodesSection({ backupCodesRemaining, onOpenRegenerateDialog, onOpenDisableDialog }) {
  return (
    <>
      <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
        <div className="flex items-center space-x-2">
          <Key className="w-5 h-5 text-slate-500" />
          <span className="text-sm text-slate-600">Backup-koder gjenstår:</span>
        </div>
        <Badge variant={backupCodesRemaining < 3 ? 'destructive' : 'secondary'}>
          {backupCodesRemaining} av 10
        </Badge>
      </div>

      {backupCodesRemaining < 3 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start space-x-2">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-700">
            Du har få backup-koder igjen. Vurder å generere nye.
          </p>
        </div>
      )}

      <div className="flex flex-wrap gap-3 pt-2">
        <Button
          variant="outline"
          onClick={onOpenRegenerateDialog}
          className="flex items-center space-x-2"
        >
          <RefreshCw className="w-4 h-4" />
          <span>Nye backup-koder</span>
        </Button>
        <Button
          variant="outline"
          onClick={onOpenDisableDialog}
          className="flex items-center space-x-2 text-red-600 hover:text-red-700 hover:bg-red-50"
        >
          <ShieldX className="w-4 h-4" />
          <span>Deaktiver App-2FA</span>
        </Button>
      </div>
    </>
  );
}

function WhyUse2FASection() {
  return (
    <div className="bg-slate-50 rounded-lg p-4">
      <h4 className="font-medium text-slate-900 mb-2">Hvorfor bruke 2FA?</h4>
      <ul className="text-sm text-slate-600 space-y-1">
        <li className="flex items-center space-x-2">
          <Check className="w-4 h-4 text-emerald-500" />
          <span>Beskytter kontoen selv om passordet blir kompromittert</span>
        </li>
        <li className="flex items-center space-x-2">
          <Check className="w-4 h-4 text-emerald-500" />
          <span>Krever tilgang til telefonen din for å logge inn</span>
        </li>
        <li className="flex items-center space-x-2">
          <Check className="w-4 h-4 text-emerald-500" />
          <span>Backup-koder for nødstilfeller</span>
        </li>
      </ul>
    </div>
  );
}
